import React, { useRef, useState } from 'react'
import {
  Grid, Dialog, DialogTitle, DialogContent, DialogActions, Button, LinearProgress, FormControl, Typography,
  IconButton
} from '@mui/material'
import { ClassNameMap, withStyles } from '@mui/styles'
import Draggable from 'react-draggable'
import { toast } from 'react-toastify'
import CloseIcon from '@mui/icons-material/Close'
import stylesheet from '../detail-container.stylesheet'
import Helper from '../../../../utils/helper'
import { PatientService } from '../../../../services/patient.service'
import DialogTitleHeaderComponent from '../common/dialog-title-header.component'
import moment from 'moment'

interface IProps {
  openUpload: any
  input: any
  onUploadClose: any
  classes: ClassNameMap
}

const CCDUploadsComponent: React.FC<IProps> = ({ openUpload, input, onUploadClose, classes }) => {
  const [selectedFile, setSelectedFile] = useState([] as any)
  const patientService = new PatientService()
  const [fileLoading, setFileLoading] = useState(false)
  const ccdUploadRef = useRef(null) as any
  const checkDisabledBtn = () => selectedFile.length <= 0
  const patientRefId = null

  const uploadFile = (e: any) => {
    let currFiles = [...selectedFile]
    Object.keys(e.target.files)?.map((i: any) => {
      currFiles = [...currFiles, e.target.files[i]]
    })
    setSelectedFile(currFiles)
  }

  const removeFile = (name: any) => setSelectedFile(selectedFile.filter((i: any) => i.name !== name))

  // Dialog reuses the same component for multiple hospitals. Hospital context
  // comes in via `input.hospital` — NOT derived from title. HIPAA-critical:
  // renaming a menu label must never silently misroute patients. If `input.hospital`
  // is missing on an Add Patients / SMS Report call we DO NOT fall back to
  // 'optum' silently; we toast an error and short-circuit the submit.
  const isSmsReport = input.title === 'Generate SMS report' || input.title === 'Generate Healthy Heart SMS report' || input.title === 'Generate ChamberCardio SMS report'
  const isAddPatients = input.title === 'Add optum patients' || input.title === 'Add Healthy Heart patients' || input.title === 'Add ChamberCardio patients'
  const hospitalFromInput: string | undefined = input.hospital
  const smsReportHospital = hospitalFromInput
  const addPatientsHospital = hospitalFromInput

  const handleSubmit = () => {
    setFileLoading(true)
    const formData = new FormData();
    (isSmsReport)
      ? formData.append(`file`, selectedFile[0])
      : selectedFile?.map((i: any, index: any) => formData.append(`file${index}`, i))

    if (input.title === 'Generate daily task list') {
      patientService.createDashboardSheet(formData).then((res: any) => {
        setFileLoading(false);
        (res.data.status === 'completed')
          ? toast.success(res.data.message || `Daily task list added successfully.`, Helper.bottom_center())
          : toast.error(res.data.message || `Failed to generate daily task list.`, Helper.bottom_center())
        onUploadClose(true)
      }).catch((err: any) => {
        toast.error(Helper.getErrorText(err), Helper.bottom_center())
        setFileLoading(false)
      })
    }
    else if (isAddPatients) {
      // HIPAA guard: fail closed if the caller didn't pass an explicit hospital.
      if (!addPatientsHospital || (addPatientsHospital !== 'optum' && addPatientsHospital !== 'healthyheart' && addPatientsHospital !== 'chambercardio')) {
        toast.error('Internal error: hospital not specified for Add Patients. Please reopen the menu and retry.', Helper.bottom_center())
        setFileLoading(false)
        onUploadClose(true)
        return
      }
      patientService.addPatientsFiles(formData, addPatientsHospital).then((res: any) => {
        setFileLoading(false);
        (res.data.status === 'completed')
          ? toast.success(res.data.message || `Patients added successfully.`, Helper.bottom_center())
          : toast.error(res.data.message || `Failed to add patient.`, Helper.bottom_center())
        onUploadClose(true)
      }).catch((err: any) => {
        toast.error(Helper.getError(err), Helper.bottom_center())
        setFileLoading(false)
      })
    } else if (isSmsReport) {
      // HIPAA guard: fail closed if the caller didn't pass an explicit hospital.
      if (!smsReportHospital || (smsReportHospital !== 'optum' && smsReportHospital !== 'healthyheart' && smsReportHospital !== 'chambercardio')) {
        toast.error('Internal error: hospital not specified for SMS report. Please reopen the menu and retry.', Helper.bottom_center())
        setFileLoading(false)
        onUploadClose(true)
        return
      }
      localStorage.setItem("SMS_Report", 'started');
      patientService.generateTxtReport(smsReportHospital, formData).then((res: any) => {
        if (res.data.message) {
          toast.error(res.data.message || `Failed to generate sms log.`, Helper.bottom_center())
          localStorage.setItem("SMS_Report", '');
          setFileLoading(false);
          onUploadClose(true)
        } else if (res.status === 200) {
          Helper.handleReportZipFile(res?.data, `AM_INTXN_${smsReportHospital}_${moment().format('YYYYMMDD')}`)
          localStorage.setItem("SMS_Report", '');
          setFileLoading(false);
          onUploadClose(true)
        }
      }).catch((err: any) => {
        toast.error(Helper.getError(err), Helper.bottom_center())
        localStorage.setItem("SMS_Report", '');
        setFileLoading(false);
        onUploadClose(true)
      })
    } else if (input.title === 'CCD elation uploads') {
      patientService.postCCDFiles(input.mrn, formData).then((res: any) => {
        setFileLoading(false);
        (res.data.status === 'completed')
          ? toast.success(res.data.message || `CCD directory files uploaded successfully.`, Helper.bottom_center())
          : toast.error(res.data.message || `Failed to upload file.`, Helper.bottom_center())
        onUploadClose(true)
      }).catch((err: any) => {
        toast.error(Helper.getError(err), Helper.bottom_center())
        setFileLoading(false)
      })
    } else if (input.title === 'Import SF Tasks') {
      patientService.importSFTasks(formData, patientRefId).then((res: any) => {
        setFileLoading(false);
        (res.data && res?.data.message)
          ? toast.success(res.data.message || `File imported successfully.`, Helper.bottom_center())
          : toast.error(res.data.message || `Failed to import file.`, Helper.bottom_center())
        onUploadClose(true)
      }).catch((err: any) => {
        toast.error(Helper.getError(err), Helper.bottom_center())
        setFileLoading(false)
      })
    }
  }

  const handleClose = (event: any, reason: any) => {
    if (reason && reason == "backdropClick" && "escapeKeyDown")
      return;
    onUploadClose(false);
  }

  return (
    <Draggable ref={ccdUploadRef} handle='.ccd-uploads-dialog'>
      <Dialog
        fullWidth={true}
        maxWidth="sm"
        open={openUpload}
        onClose={handleClose}
        aria-labelledby={'CCD elation uploads'}
        hideBackdrop
        disableScrollLock
        onKeyUp={(e) => {
          e.preventDefault();
          if (e.key === 'Enter' && !checkDisabledBtn())
            handleSubmit()
        }}
        sx={{
          position: "absolute",
          left: '15%',
          top: '15%'
        }}
      >
        <DialogTitle className={classes.addPatientQuestionHeaderSection} classes={{ root: 'ccd-uploads-dialog' }}>
          <DialogTitleHeaderComponent title={input.title} handleDialogClose={handleClose} />
        </DialogTitle>
        <Grid item>{fileLoading && <LinearProgress />}</Grid>
        <DialogContent className={classes.addPatientDialogContent} sx={{ overflowY: 'auto' }}>
          <Grid container spacing={2} className={classes.containerPadding}>
            <Grid item md={12} xs={12}>
              <Grid item>
                <FormControl>
                  <input
                    accept={input.accept}
                    style={{ display: 'none' }}
                    id="upload-file"
                    type="file"
                    onChange={uploadFile}
                    disabled={fileLoading}
                    multiple={isSmsReport ? false : true}
                  />
                  <label htmlFor="upload-file">
                    <Button component="span" disabled={fileLoading} variant="outlined" size="small">
                      {isSmsReport
                        ? (smsReportHospital === 'healthyheart' ? 'Choose Healthy Heart SMS log file' : 'Choose SMS log file')
                        : isAddPatients
                          ? (addPatientsHospital === 'healthyheart' ? 'Choose Healthy Heart patient log file' : 'Choose optum patient log file')
                          : input.title === 'Generate daily task list'
                            ? 'Choose files' : 'Choose file'
                      }
                    </Button>
                  </label>
                </FormControl>
              </Grid>
              <Grid item xs>
                <Typography sx={{ fontSize: '0.75rem' }} component="span" color="grey">
                  <i>{input.title === 'Generate daily task list'
                    ? `Please select Bookings file, Bp report combined file for gather and optum, Medication record file, Optum patient log file, Gather patient log file, Elation patient list export for gather, Elation patient list export for optum, Tracking file for previous day, SF patient report file, Task patient report file, Medication record addendum file`
                    : `Please select ${input?.accept || ''} file.`
                  }
                  </i>
                </Typography>
              </Grid>
              <Grid item xs pt={1}>
                {selectedFile && selectedFile.map((i: any) => <Grid container>
                  <Grid item xs={6}>
                    <Typography pt={0.5} variant='body2' className={classes.desc}>{i.name}</Typography>
                  </Grid>
                  <Grid item xs={5}>
                    <IconButton size="small" disabled={fileLoading} onClick={() => removeFile(i.name)}>
                      <CloseIcon className={classes.editIcon} />
                    </IconButton>
                  </Grid>
                </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Grid container spacing={2} className={classes.containerPadding} justifyContent="flex-end">
            <Typography sx={{ fontSize: '0.75rem', paddingTop: '0.2rem' }} component="span" color="grey">
              <i>{input.title === 'Generate daily task list'
                ? '(Once you click on Submit button, file generation will be started. You can download file after 30 minutes.)'
                : ''
              }
              </i>
            </Typography>
            <Grid item>
              <Button
                variant="outlined"
                size="small"
                disabled={fileLoading || checkDisabledBtn()}
                onClick={() => handleSubmit()}
                component="label"
              >
                Submit
              </Button>
            </Grid>
            <Grid item>
              <Button variant="outlined" size="small" onClick={onUploadClose}>
                Cancel
              </Button>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>
    </Draggable>
  )
}

export default withStyles(stylesheet)(CCDUploadsComponent)